<!-- src/views/HealthandSafety/IncidentInvestigationFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <FormHeader backTo="HealthSafetyFormsList" backLabel="Back to H&S List" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">
        
        <!-- 🛡️ PREMIUM HEADER CARD -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
          <div class="absolute top-0 right-0 p-12 opacity-10">
            <MagnifyingGlassCircleIcon class="h-32 w-32 text-white" />
          </div>
          <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Incident Investigation Registry</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">
                SS-IIF Protocol • Root Cause Analysis & Evidence Synthesis
              </p>
            </div>
            <div class="bg-white/5 px-6 py-3 rounded-2xl border border-white/10 flex items-center gap-3 backdrop-blur-md">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full animate-pulse"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest text-left">Admin Command<br><span class="text-[#20FCF1]">Authoritative Tier</span></span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">
          
          <!-- ERROR/LOADING STATES -->
          <div v-if="submissionError" class="mb-8 p-6 bg-rose-50 border-l-8 border-rose-500 rounded-2xl flex items-center gap-4 animate-shake">
            <ExclamationTriangleIcon class="h-8 w-8 text-rose-600 flex-shrink-0" />
            <div class="min-w-0">
               <p class="text-xs font-black text-rose-900 uppercase tracking-widest mb-1">Registry Handshake Failure</p>
               <p class="text-[11px] text-rose-700 font-bold uppercase tracking-tight leading-tight">{{ submissionError }}</p>
            </div>
          </div>

          <div v-if="isLoading" class="flex flex-col items-center py-32 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-200">
            <div class="relative">
              <div class="w-16 h-16 border-4 border-[#20FCF1]/20 rounded-full"></div>
              <div class="w-16 h-16 border-4 border-[#20FCF1] border-t-transparent rounded-full animate-spin absolute top-0"></div>
            </div>
            <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mt-8">Synchronising Safety Repositories...</p>
          </div>

          <form v-else @submit.prevent="handleSubmit" class="space-y-16">
            
            <!-- STEP 1: SOURCE LINKAGE (SYNTHESIZED HANDSHAKE) -->
            <section class="space-y-8">
              <div class="flex items-center gap-4">
                <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                  <LinkIcon class="h-7 w-7" />
                </div>
                <div>
                  <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">1. Source Linkage</h2>
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Associate investigation with existing incident records</p>
                </div>
              </div>

              <div class="grid grid-cols-1 lg:grid-cols-12 gap-4 bg-slate-50 p-6 rounded-[40px] border border-slate-100 shadow-inner">
                
                <!-- SELECTORS -->
                <div class="lg:col-span-2 space-y-2">
                  <label class="form-label-compact">Accident Ref <span class="text-rose-500">*</span></label>
                  <select v-model="formData.linkedAccidentRef" required class="form-input-compact" @change="handleReferenceChange">
                    <option value="" disabled>Select Ref</option>
                    <option v-for="report in accidentReports" :key="report.id" :value="report.referenceNumber">
                      {{ report.referenceNumber }}
                    </option>
                  </select>
                </div>

                <div class="lg:col-span-2 space-y-2">
                  <label class="form-label-compact">Witness Ref <span class="text-rose-500">*</span></label>
                  <!-- 🛡️ MANDATORY WITNESS REQUIREMENT -->
                  <select v-model="formData.linkedWitnessRef" required class="form-input-compact">
                    <option value="" disabled>Select Ref</option>
                    <option v-for="witness in witnessStatements" :key="witness.id" :value="witness.referenceNumber">
                      {{ witness.referenceNumber }}
                    </option>
                  </select>
                </div>

                <!-- CONSOLIDATED MIDNIGHT HANDSHAKE BLOCK -->
                <div class="lg:col-span-8 space-y-2 border-l border-slate-200 pl-4">
                  <label class="form-label-compact">Handshake Data: Identity • Date • Worksite Location</label>
                  <div class="form-input-display bg-[#0A1630] text-[#20FCF1] font-black flex items-center gap-4 px-6 overflow-hidden">
                    <span class="shrink-0 uppercase text-[12px] tracking-tight">
                        {{ formData.subjectUser || 'Personnel' }}
                    </span>
                    <span class="text-white/20 shrink-0">|</span>
                    <span class="shrink-0 text-[12px]">
                        {{ formData.accidentDate || '00/00/0000' }}
                    </span>
                    <span class="text-white/20 shrink-0">|</span>
                    <span class="truncate uppercase text-[11px] font-bold">
                        {{ formData.accidentLocation || 'Awaiting Selection' }}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <!-- STEP 2: INCIDENT NARRATIVE (AUTO-POPULATED HANDSHAKE) -->
            <section class="space-y-8">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-4">
                  <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                    <DocumentTextIcon class="h-7 w-7" />
                  </div>
                  <div>
                    <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">2. Incident Narrative</h2>
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Sequential analysis of the event and immediate outcome</p>
                  </div>
                </div>
                <!-- SYNC INDICATOR -->
                <div v-if="formData.linkedAccidentRef" class="hidden sm:flex items-center gap-2 bg-[#20FCF1]/10 px-4 py-2 rounded-xl border border-[#20FCF1]/20 animate-fadeIn">
                   <ArrowPathIcon class="h-3 w-3 text-[#20FCF1] animate-spin" />
                   <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Synthesised from Accident Registry</span>
                </div>
              </div>

              <div class="bg-slate-50 p-10 rounded-[40px] space-y-10 border border-slate-100 shadow-inner text-left">
                <div>
                  <label class="form-label">Task Context <span class="text-rose-500">*</span></label>
                  <textarea v-model="formData.taskAtTime" rows="3" required class="form-input" placeholder="Describe the task being performed..."></textarea>
                </div>
                <div>
                  <label class="form-label">Sequence of Events <span class="text-rose-500">*</span></label>
                  <textarea v-model="formData.sequenceOfEvents" rows="4" required class="form-input" placeholder="Describe the sequence of events..."></textarea>
                </div>
                <div>
                  <label class="form-label">Immediate Response & Injury Outcome <span class="text-rose-500">*</span></label>
                  <textarea v-model="formData.postIncidentOutcome" rows="4" required class="form-input" placeholder="Describe the immediate response and outcome..."></textarea>
                </div>
              </div>
            </section>

            <!-- STEP 3: INVESTIGATIVE VECTORS -->
            <section class="space-y-8">
              <div class="flex items-center gap-4">
                <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                  <AdjustmentsHorizontalIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">3. Investigative Vectors</h2>
              </div>

              <div class="space-y-6">
                <div v-for="vector in investigationVectors" :key="vector.id" class="overflow-hidden transition-all duration-500">
                  <button type="button" @click="vector.isActive = !vector.isActive" :class="vector.isActive ? 'bg-[#0A1630] border-[#20FCF1]' : 'bg-white border-slate-200'" class="w-full flex items-center justify-between p-8 rounded-[32px] border-2 shadow-sm transition-all group">
                    <div class="flex items-center gap-6 text-left">
                       <component :is="vector.icon" :class="vector.isActive ? 'text-[#20FCF1]' : 'text-slate-400'" class="h-7 w-7" />
                       <span :class="vector.isActive ? 'text-white' : 'text-[#0A1630]'" class="font-black uppercase text-sm tracking-widest">{{ vector.title }}</span>
                    </div>
                    <div :class="vector.isActive ? 'bg-[#20FCF1] rotate-180' : 'bg-slate-100'" class="p-2.5 rounded-full transition-all duration-500">
                       <ChevronDownIcon :class="vector.isActive ? 'text-[#0A1630]' : 'text-slate-400'" class="h-5 w-5" />
                    </div>
                  </button>

                  <div v-if="vector.isActive" class="p-10 bg-[#F8FAFC] border-x-2 border-b-2 border-slate-100 rounded-b-[40px] mx-6 animate-slideDown shadow-inner">
                    <template v-if="vector.id === 'systemic'">
                        <div v-for="header in systemicSubHeaders" :key="header" class="mb-10 last:mb-0">
                           <h4 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-6 border-l-4 border-[#20FCF1] pl-4 text-left">{{ header }}</h4>
                           <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
                              <div v-for="(option, optIdx) in filterOptionsByHeader(vector.options, header)" :key="optIdx" class="flex items-center justify-between p-6 bg-white rounded-2xl shadow-sm border border-slate-100 group transition-all">
                                <span class="text-[11px] font-black uppercase text-slate-600 tracking-tight leading-tight pr-6">{{ option.text }}</span>
                                <div class="relative inline-block w-14 h-7 align-middle select-none transition duration-200 ease-in">
                                  <input type="checkbox" v-model="formData.selectedFactors" :value="`${vector.title} (${header}): ${option.text}`" class="absolute block w-7 h-7 rounded-full bg-white border-4 appearance-none cursor-pointer checked:right-0 right-7 checked:border-[#20FCF1] transition-all duration-300 z-10" />
                                  <label class="block overflow-hidden h-7 rounded-full bg-slate-200 cursor-pointer group-hover:bg-slate-300 transition-colors"></label>
                                </div>
                              </div>
                           </div>
                        </div>
                    </template>
                    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
                       <div v-for="(option, optIdx) in vector.options" :key="optIdx" class="flex items-center justify-between p-6 bg-white rounded-2xl shadow-sm border border-slate-100">
                          <span class="text-[11px] font-black uppercase text-slate-600 tracking-tight pr-6">{{ option.text }}</span>
                          <div class="relative inline-block w-14 h-7 align-middle select-none transition duration-200 ease-in">
                            <input type="checkbox" v-model="formData.selectedFactors" :value="`${vector.title}: ${option.text}`" class="absolute block w-7 h-7 rounded-full bg-white border-4 appearance-none cursor-pointer checked:right-0 right-7 checked:border-[#20FCF1] transition-all duration-300 z-10" />
                            <label class="block overflow-hidden h-7 rounded-full bg-slate-200 cursor-pointer transition-colors"></label>
                          </div>
                       </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- STEP 4: EVIDENCE SYNTHESIS -->
            <section class="space-y-8">
              <div class="flex items-center gap-4">
                <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                  <ArchiveBoxIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">4. Evidence Synthesis</h2>
              </div>

              <div class="bg-slate-50 p-10 rounded-[40px] space-y-10 border border-slate-100 shadow-inner text-left">
                <div class="space-y-4">
                   <label class="form-label">Investigation Evidence Vault (PNG, JPG, PDF, DOCX)</label>
                   <div class="bg-white border-2 border-dashed border-slate-200 rounded-[32px] p-12 text-center hover:border-[#20FCF1] transition-all group relative">
                      <input type="file" multiple accept=".png,.jpg,.jpeg,.pdf,.docx" @change="handleFileUpload" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20" />
                      <div class="relative z-10 space-y-4">
                        <div class="bg-[#EBFDF8] w-20 h-20 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                           <PaperClipIcon class="h-8 w-8 text-[#20FCF1]" />
                        </div>
                        <p class="text-sm font-black text-[#0A1630] uppercase">Synchronise Evidence Files</p>
                      </div>
                   </div>
                   <div v-if="evidenceFiles.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                      <div v-for="(file, idx) in evidenceFiles" :key="idx" class="bg-white p-4 rounded-2xl border border-slate-200 flex items-center justify-between gap-4">
                         <div class="flex items-center gap-3 truncate">
                            <DocumentIcon class="h-4 w-4 text-[#20FCF1]" />
                            <span class="text-[10px] font-black uppercase text-[#0A1630] truncate">{{ file.name }}</span>
                         </div>
                         <button @click="removeFile(idx)" type="button" class="text-rose-500"><XMarkIcon class="h-4 w-4" /></button>
                      </div>
                   </div>
                </div>
                <div>
                  <label class="form-label">Investigation Executive Summary <span class="text-rose-500">*</span></label>
                  <textarea v-model="formData.summary" rows="5" required class="form-input" placeholder="Evidence-based consolidated summary..."></textarea>
                </div>
              </div>
            </section>

            <!-- STEP 5: RECOMMENDATIONS -->
            <section class="space-y-8">
              <div class="flex items-center gap-4">
                <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                  <ShieldCheckIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">5. Safety Recommendations</h2>
              </div>
              <div class="bg-slate-50 p-10 rounded-[40px] space-y-10 border border-slate-100 shadow-inner text-left">
                <div>
                  <label class="form-label">Lessons Learned <span class="text-rose-500">*</span></label>
                  <textarea v-model="formData.lessonsLearned" rows="4" required class="form-input" placeholder="Improvements identified..."></textarea>
                </div>
                <div>
                  <label class="form-label">Corrective & Preventative Actions (CAPA) <span class="text-rose-500">*</span></label>
                  <textarea v-model="formData.recommendations" rows="6" required class="form-input" placeholder="Specific, Measurable Actions..."></textarea>
                </div>
              </div>
            </section>

            <!-- STEP 6: AUTHORITATIVE SEAL -->
            <section class="space-y-10">
              <div class="flex items-center gap-4">
                <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                  <FingerPrintIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">6. Authoritative Seal</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                <div class="bg-[#0A1630] p-12 rounded-[48px] text-white shadow-2xl flex flex-col items-center border-b-8 border-[#20FCF1]">
                  <div class="w-full bg-white p-6 rounded-[32px] mb-8">
                    <Vue3Signature ref="sigPad" :sigOption="sigOptions" h="200px" w="100%" class="rounded-2xl" />
                  </div>
                  <div class="flex gap-4 w-full">
                    <button type="button" @click="saveSig" class="flex-1 bg-[#20FCF1] text-[#0A1630] py-5 rounded-2xl font-black uppercase text-xs tracking-widest">Lock Signature</button>
                    <button type="button" @click="clearSig" class="px-8 bg-white/10 text-white py-5 rounded-2xl font-black uppercase text-xs tracking-widest">Clear</button>
                  </div>
                </div>

                <div class="space-y-8 text-left">
                  <div class="bg-white p-10 rounded-[40px] border border-slate-200 shadow-sm relative">
                    <div class="flex items-center gap-6 mb-8">
                       <div v-if="formData.signatureDataUrl" class="bg-slate-50 p-3 rounded-[24px] border border-slate-100 shadow-inner">
                          <img :src="formData.signatureDataUrl" class="h-16 object-contain grayscale" />
                       </div>
                       <div v-else class="h-20 w-32 bg-slate-50 rounded-[24px] animate-pulse"></div>
                       <div>
                          <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest mb-1">Investigating Official</p>
                          <p class="text-xl font-black text-slate-500 uppercase">{{ formData.adminName || 'Authorising...' }}</p>
                       </div>
                    </div>
                  </div>
                  <div class="bg-[#20FCF1]/5 p-10 rounded-[48px] border border-[#20FCF1]/20 flex items-center gap-6">
                    <div class="bg-[#20FCF1] p-4 rounded-2xl shadow-xl"><DocumentCheckIcon class="h-7 w-7 text-[#0A1630]" /></div>
                    <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest leading-relaxed">Investigation Synchronisation Active.</p>
                  </div>
                </div>
              </div>
            </section>

            <!-- SUBMISSION ENGINE -->
            <div class="pt-16 border-t border-slate-100 flex flex-col md:flex-row gap-6 no-print">
              <button type="submit" :disabled="isSubmitting || !isFormValid" class="flex-1 bg-[#20FCF1] text-[#0A1630] py-10 rounded-[40px] text-2xl font-black uppercase tracking-widest shadow-2xl hover:scale-[1.02] disabled:opacity-50">
                <span v-if="isSubmitting" class="flex items-center justify-center gap-4">
                  <div class="animate-spin h-8 w-8 border-4 border-[#0A1630] border-t-transparent rounded-full"></div>
                  Filing Audit Record...
                </span>
                <span v-else>Formalise Investigation</span>
              </button>
              <button type="button" @click="router.back()" class="px-16 py-10 bg-slate-200 text-slate-700 rounded-[40px] text-xl font-black uppercase tracking-widest">Discard</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- SUCCESS OVERLAY -->
    <div v-if="successRef" class="fixed inset-0 z-[200] flex items-center justify-center p-6 backdrop-blur-2xl bg-[#0A1630]/90 animate-fadeIn">
       <div class="bg-white p-16 rounded-[60px] shadow-2xl max-w-xl w-full text-center space-y-10 border-t-[12px] border-[#20FCF1]">
          <div class="bg-[#EBFDF8] w-28 h-28 rounded-full flex items-center justify-center mx-auto shadow-inner"><CheckCircleIcon class="h-14 w-14 text-[#20FCF1]" /></div>
          <div>
            <h2 class="text-4xl font-black text-[#0A1630] uppercase tracking-tight">Registry Synchronised</h2>
            <div class="mt-10 p-10 bg-slate-50 rounded-[40px] border border-slate-100 inline-block shadow-inner">
               <p class="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Audit Reference</p>
               <p class="text-4xl font-black text-[#0A1630] tracking-tighter">{{ successRef }}</p>
            </div>
          </div>
          <button @click="router.push({ name: 'HealthSafetyFormsList' })" class="w-full bg-[#0A1630] text-white py-8 rounded-[32px] font-black uppercase tracking-widest shadow-2xl hover:scale-105 transition-all">Return to Command</button>
       </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { db, auth, firebaseApp, storage } from '@/firebaseConfig';
import { collection, query, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Vue3Signature from 'vue3-signature';
import { useUserStore } from '@/stores/userStore';
import FormHeader from '@/components/FormHeader.vue';

import { 
  LinkIcon, AdjustmentsHorizontalIcon, ShieldCheckIcon,
  MagnifyingGlassCircleIcon, ExclamationTriangleIcon, ChevronDownIcon,
  UserGroupIcon, BoltIcon, BeakerIcon, DocumentTextIcon,
  FingerPrintIcon, DocumentCheckIcon, CheckCircleIcon,
  ArchiveBoxIcon, PaperClipIcon, XMarkIcon, DocumentIcon,
  ArrowPathIcon
} from '@heroicons/vue/24/outline';

const router = useRouter();
const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2');

const isLoading = ref(true);
const isSubmitting = ref(false);
const submissionError = ref('');
const successRef = ref('');
const sigPad = ref(null);
const sigOptions = { penColor: "#0A1630", backgroundColor: "rgba(255,255,255,0)" };

const accidentReports = ref([]);
const witnessStatements = ref([]);
const evidenceFiles = ref([]);

const systemicSubHeaders = ['Planning & Risk Management', 'Competence & Training', 'Supervision & Monitoring', 'Resources & Standards', 'Rail / Construction Specific'];

const investigationVectors = ref([
  { id: 'human', title: 'Subject Behaviour & Human Factors', isActive: false, icon: UserGroupIcon, docId: 'investigationHumanFactors', options: [] },
  { id: 'technical', title: 'Technical & Equipment Integrity', isActive: false, icon: BoltIcon, docId: 'investigationTechnicalFactors', options: [] },
  { id: 'systemic', title: 'Systemic & Management Planning', isActive: false, icon: BeakerIcon, docId: 'investigationSystemicFactors', options: [] }
]);

const formData = reactive({
  linkedAccidentRef: '', linkedWitnessRef: '', subjectUser: '',
  accidentDate: '', accidentLocation: '',
  taskAtTime: '', sequenceOfEvents: '', postIncidentOutcome: '',
  selectedFactors: [], summary: '', recommendations: '', lessonsLearned: '',
  adminName: '', signatureDataUrl: '', evidenceUrls: [], submittedAt: null
});

const handleFileUpload = (e) => {
  const selected = Array.from(e.target.files);
  evidenceFiles.value = [...evidenceFiles.value, ...selected];
};

const removeFile = (idx) => { evidenceFiles.value.splice(idx, 1); };

/** 🛡️ DEEP HANDSHAKE: Narrative Auto-Population */
const handleReferenceChange = () => {
    if (formData.linkedAccidentRef) {
        const match = accidentReports.value.find(r => r.referenceNumber === formData.linkedAccidentRef);
        if (match) {
            formData.subjectUser = match.subjectName;
            formData.accidentDate = match.displayDate;
            formData.accidentLocation = match.displayLocation;
            
            // 🛡️ NARRATIVE SYNTHESIS: Replicated from Accident Registry
            formData.taskAtTime = match.taskContext || '';
            formData.sequenceOfEvents = match.sequenceOfEvents || '';
            formData.postIncidentOutcome = match.immediateOutcome || '';
        }
    }
};

const filterOptionsByHeader = (options, header) => {
    return options.filter(o => {
        const text = String(o.text || '').toLowerCase();
        const head = String(header).toLowerCase();
        return text.includes(head.split(' ')[0]) || header === 'Planning & Risk Management';
    });
};

const isFormValid = computed(() => {
  // 🛡️ MANDATORY LINKAGE CHECK
  return formData.linkedAccidentRef && formData.linkedWitnessRef && formData.summary && formData.lessonsLearned && formData.recommendations && formData.signatureDataUrl;
});

const loadSourceData = async () => {
  isLoading.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) throw new Error("Client Identity Context missing.");

    const subSnap = await getDocs(query(collection(db, 'healthsafetyFormSubmissions'), where('clientId', '==', cid)));
    const allDocs = subSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    accidentReports.value = allDocs.filter(d => d.formIdentifier === 'accidentForm')
      .map(d => {
        const nested = d.formData || {};
        const resolvedName = d.userName || nested.fullName || nested.reporterName || 'Personnel';
        const rawDate = nested.accidentDateTime || nested.dateTime || d.submittedAt;
        const possibleDate = rawDate ? new Date(rawDate.seconds ? rawDate.seconds * 1000 : rawDate).toLocaleDateString('en-GB') : 'N/A';
        
        return {
          id: d.id,
          referenceNumber: d.humanReadableId || d.referenceNumber || d.id,
          displayLocation: nested.location || d.location || 'Site',
          displayDate: possibleDate,
          subjectName: resolvedName,
          
          /** 🛡️ DEEP HANDSHAKE: Sequential Mapping */
          taskContext: nested.taskContext || '',
          sequenceOfEvents: nested.sequenceOfEvents || '',
          immediateOutcome: nested.immediateResponseOutcome || ''
        };
      }).sort((a,b) => b.referenceNumber.localeCompare(a.referenceNumber)); // Sort Descending

    witnessStatements.value = allDocs.filter(d => d.formIdentifier === 'accidentWitnessStatementForm')
      .map(d => ({
          id: d.id,
          referenceNumber: d.humanReadableId || d.referenceNumber || d.id,
          displayWitnessName: d.formData?.witnessName || d.userName || 'Personnel',
          submittedAt: d.submittedAt
      })).sort((a,b) => b.referenceNumber.localeCompare(a.referenceNumber)); // 🛡️ REVERSE REGISTRY SORTING

    for (const v of investigationVectors.value) {
      const snap = await getDoc(doc(db, 'formOptions', v.docId));
      if (snap.exists()) v.options = snap.data()[cid] || snap.data().global || [];
    }
    
    if (auth.currentUser) {
      const pSnap = await getDoc(doc(db, 'userProfiles', auth.currentUser.uid));
      if (pSnap.exists()) {
        const p = pSnap.data();
        formData.adminName = `${p.firstName} ${p.lastName}`;
        formData.signatureDataUrl = p.signatureDataUrl || p.signatureUrl || '';
      }
    }
  } catch (err) { 
      console.error("Registry Load Failure:", err); 
      submissionError.value = err.message;
  } finally { 
      isLoading.value = false; 
  }
};

onMounted(loadSourceData);

const saveSig = () => { if (sigPad.value && !sigPad.value.isEmpty()) formData.signatureDataUrl = sigPad.value.save('image/png'); };
const clearSig = () => { if (sigPad.value) sigPad.value.clear(); formData.signatureDataUrl = ''; };

const handleSubmit = async () => {
  if (!isFormValid.value) return;
  isSubmitting.value = true;
  submissionError.value = '';
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    const urls = [];
    for (const file of evidenceFiles.value) {
        const path = `clients/${cid}/investigation_evidence/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
        const sRef = storageRef(storage, path);
        await uploadBytes(sRef, file);
        const url = await getDownloadURL(sRef);
        urls.push({ name: file.name, url });
    }
    formData.evidenceUrls = urls;

    const result = await httpsCallable(functions, 'submitGenericForm')({
      formIdentifier: 'hsInvestigationRegistry', 
      targetCollection: 'healthsafetyFormSubmissions',
      submissionData: { ...formData, activeVectors: investigationVectors.value.filter(v => v.isActive).map(v => v.title) }
    });
    if (result.data.success) successRef.value = result.data.referenceNumber;
  } catch (err) { 
    submissionError.value = err.message; 
    isSubmitting.value = false; 
  }
};
</script>

<style scoped>
.form-input { @apply block w-full px-6 py-5 bg-white border border-slate-200 rounded-[20px] text-slate-700 shadow-inner transition-all focus:ring-2 focus:ring-[#20FCF1] font-bold; }
.form-input-compact { @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm text-xs font-black appearance-none outline-none focus:ring-2 focus:ring-[#20FCF1]; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%230F172A'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem; }
.form-input-display { @apply block w-full px-4 py-3 border border-slate-200 rounded-xl text-xs shadow-sm h-[42px] flex items-center overflow-hidden; }
.form-label { @apply block text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-4 ml-1; }
.form-label-compact { @apply block text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 ml-1; }
.animate-slideDown { animation: slideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes slideDown { from { opacity: 0; transform: translateY(-15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-shake { animation: shake 0.6s cubic-bezier(.36,.07,.19,.97) both; }
@keyframes shake { 10%, 90% { transform: translate3d(-1px, 0, 0); } 20%, 80% { transform: translate3d(2px, 0, 0); } 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); } 40%, 60% { transform: translate3d(4px, 0, 0); } }
</style>